/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from 'react';import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Gavel, ShieldCheck, Warning, CheckCircle, ArrowUpRight, Hourglass, Question, XCircle } from '@phosphor-icons/react';
import TI from '../../components/brand/TI';
import { HaleWordmark } from '../../components/HaleBrand';
import SummaryCards from '../../components/hale-review/SummaryCards';
import AuditTimeline, { haleStageIndex } from '../../components/hale-review/AuditTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IVORY = '#F5F1E8';
const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';

/* /admin/hale/decisions — Hale program decisions console (admin only).
 *
 * Implements the Hale program authority surface (separate from the AG
 * scientific authority surface):
 *   - Pull submissions where AG has produced a recommendation
 *   - Read the AG audit (released findings + recommendation + conditions)
 *   - Make the final Hale program decision (approve / hold / reject)
 *   - Pick the track (Reviewed™ / Verified™ / Both)
 *   - Issue or hold the badge
 *
 * AG reviewer internal notes are NOT visible on this surface — only the
 * released findings + recommendation. */

function StatusPill({ value, options, accent = CORAL }) {
  if (!value) return <span className="text-[11px]" style={{ color: MUTED }}>—</span>;
  const opt = (options || []).find(o => o.id === value);
  return (
    <span
      className="inline-flex items-center px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-[0.18em]"
      style={{ backgroundColor: 'rgba(11,20,38,0.06)', color: accent }}
    >
      {opt?.label || value}
    </span>
  );
}

function DecisionEditor({ app, onDecided }) {
  const [track, setTrack] = useState('hale_reviewed');
  const [notes, setNotes] = useState('');
  const [issueBadge, setIssueBadge] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Reset transient form state when a different application is picked so
  // notes from one row don't leak into another.
  useEffect(() => {
    setTrack('hale_reviewed');
    setNotes(''); setIssueBadge(true);
    setError(null); setSuccess(null);
  }, [app.id]);

  const decisionMade = ['hale_approved', 'hale_hold', 'hale_rejected', 'badge_issued']
    .includes(app.hale?.hale_status);

  // Submit a specific decision (one of the 5 action buttons). Auto-fills
  // track for the two approve variants and clears it for the rest.
  const submitDecision = async ({ decision: dec, track: tr, issueBadge: ib }) => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/hale-review/application/${app.id}/hale/decide`,
        {
          decision: dec,
          track: dec === 'hale_approved' ? tr : null,
          decision_notes: notes,
          issue_badge: dec === 'hale_approved' && ib,
        },
        { withCredentials: true },
      );
      onDecided(r.data);
      const ok = {
        hale_approved:         'Brand admitted to Hale program.',
        hale_hold:             'Submission held pending conditions.',
        hale_rejected:         'Submission not admitted.',
        request_clarification: 'Clarification requested — submission routed back to AG.',
      }[dec] || 'Hale decision recorded.';
      setSuccess(ok);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Decision failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid={`decision-editor-${app.id}`}>
      {/* AG audit summary panel (read-only) */}
      <div className="rounded-sm border p-5" style={{ borderColor: RULE, backgroundColor: 'rgba(11,20,38,0.025)' }}>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>
          AG audit · received {app.ag?.completed_at ? new Date(app.ag.completed_at).toLocaleDateString() : '—'}
        </p>
        <p className="text-base font-semibold mb-1" style={{ color: SLATE }}>
          {app.ag?.recommendation_label || 'No recommendation yet'}
        </p>
        {(app.ag?.recommendation_conditions || []).length > 0 && (
          <ul className="mt-2 ml-4 list-disc text-sm space-y-1" style={{ color: MUTED }} data-testid="decision-ag-conditions">
            {app.ag.recommendation_conditions.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        )}
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] mt-4 mb-1" style={{ color: MUTED }}>
          Released findings
        </p>
        <pre className="text-sm whitespace-pre-wrap font-sans" style={{ color: SLATE }} data-testid="decision-ag-findings">
          {app.ag?.released_findings || '—'}
        </pre>
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] mt-4" style={{ color: MUTED }}>
          Reviewer · {app.ag?.reviewer_email || '—'}
        </p>
      </div>

      {/* Decision form — notes + (when approving) track + badge issuance toggle.
         The 5 explicit action buttons live below so each decision path
         is a one-click commit; the legacy decision dropdown is retained
         as a read-only summary of the picked path. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>
            Approval track (used by Hale Reviewed™ / Verified™)
          </p>
          <select
            value={track}
            onChange={(e) => setTrack(e.target.value)}
            className="w-full text-sm p-2.5 rounded-sm border bg-white"
            style={{ borderColor: RULE, color: SLATE }}
            disabled={decisionMade}
            data-testid="decision-track"
          >
            <option value="hale_reviewed">Hale Reviewed™</option>
            <option value="hale_verified">Hale Verified™</option>
            <option value="both">Reviewed™ + Verified™</option>
          </select>
        </div>
        <label className="flex items-end gap-2 text-sm" style={{ color: SLATE }}>
          <input
            type="checkbox"
            checked={issueBadge}
            onChange={(e) => setIssueBadge(e.target.checked)}
            disabled={decisionMade}
            data-testid="decision-issue-badge"
          />
          Issue badge immediately on approval (recommended)
        </label>
      </div>
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>
          Decision notes (visible to AG, not to brand)
        </p>
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full text-sm p-3 rounded-sm border bg-white"
          style={{ borderColor: RULE, color: SLATE }}
          disabled={decisionMade}
          data-testid="decision-notes"
        />
      </div>

      {error && (
        <div className="rounded-sm border p-3 text-[13px]"
             style={{ borderColor: 'rgba(176,49,45,0.4)', backgroundColor: 'rgba(176,49,45,0.05)', color: '#B0312D' }}
             data-testid="decision-error">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-sm border p-3 text-[13px]"
             style={{ borderColor: RULE, backgroundColor: 'rgba(22,98,75,0.06)', color: '#16624B' }}
             data-testid="decision-success">
          {success}
        </div>
      )}

      {/* Decision actions — five explicit one-click paths. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="decision-actions">
        <button
          onClick={() => submitDecision({ decision: 'hale_approved', track: 'hale_reviewed', issueBadge })}
          disabled={saving || decisionMade}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-[11px] font-mono uppercase tracking-wider transition-transform hover:-translate-y-px disabled:opacity-50"
          style={{ backgroundColor: CORAL, color: SLATE }}
          data-testid="decision-accept-reviewed"
        >
          <CheckCircle size={14} weight="fill" /> Accept into Reviewed™
        </button>
        <button
          onClick={() => submitDecision({ decision: 'hale_approved', track: 'hale_verified', issueBadge })}
          disabled={saving || decisionMade}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-[11px] font-mono uppercase tracking-wider transition-transform hover:-translate-y-px disabled:opacity-50 border"
          style={{ backgroundColor: IVORY, color: SLATE, borderColor: SLATE }}
          data-testid="decision-accept-verified"
        >
          <ShieldCheck size={14} weight="fill" /> Accept into Verified™
        </button>
        <button
          onClick={() => submitDecision({ decision: 'hale_hold' })}
          disabled={saving || decisionMade}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-[11px] font-mono uppercase tracking-wider border disabled:opacity-50"
          style={{ borderColor: '#A07A14', color: '#A07A14', backgroundColor: 'rgba(212,160,23,0.06)' }}
          data-testid="decision-hold"
        >
          <Hourglass size={14} weight="fill" /> Hold pending conditions
        </button>
        <button
          onClick={() => submitDecision({ decision: 'hale_rejected' })}
          disabled={saving || decisionMade}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-[11px] font-mono uppercase tracking-wider border disabled:opacity-50"
          style={{ borderColor: '#B0312D', color: '#B0312D', backgroundColor: 'rgba(176,49,45,0.04)' }}
          data-testid="decision-reject"
        >
          <XCircle size={14} weight="fill" /> Reject / Not admitted
        </button>
        <button
          onClick={() => submitDecision({ decision: 'request_clarification' })}
          disabled={saving || decisionMade}
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-[11px] font-mono uppercase tracking-wider border disabled:opacity-50"
          style={{ borderColor: MUTED, color: MUTED, backgroundColor: 'transparent' }}
          data-testid="decision-clarify"
        >
          <Question size={14} weight="bold" /> Request clarification from AG (routes back to reviewer)
        </button>
      </div>
      {decisionMade && (
        <p className="text-[11px] font-mono uppercase tracking-wider mt-1" style={{ color: MUTED }} data-testid="decision-already">
          A Hale decision is already on record for this submission.
        </p>
      )}

      {app.badge?.issued_at && (
        <div className="mt-6 rounded-sm border p-4 flex items-center gap-3"
             style={{ borderColor: RULE, backgroundColor: IVORY }}
             data-testid="decision-badge-preview">
          <ShieldCheck size={22} weight="fill" style={{ color: CORAL }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: SLATE }}>{app.badge.label} issued</p>
            <p className="text-[11px] font-mono uppercase tracking-wider" style={{ color: MUTED }}>
              Issued {new Date(app.badge.issued_at).toLocaleDateString()} ·{' '}
              <a className="underline" href={`${API_URL}${app.badge.embed_url}`} target="_blank" rel="noreferrer" data-testid="decision-badge-link">View SVG</a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HaleDecisionsPage() {
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [q, cfg] = await Promise.all([
        axios.get(`${API_URL}/api/hale-review/queue/hale`, { withCredentials: true }),
        axios.get(`${API_URL}/api/hale-review/config`, { withCredentials: true }),
      ]);
      setItems(q.data.items || []);
      setOptions(cfg.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load Hale decisions queue.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openApp = useMemo(() => items.find(a => a.id === openId), [items, openId]);
  const handleDecided = (updated) => {
    setItems(arr => arr.map(a => a.id === updated.id ? updated : a));
  };

  // Decision summary counts — computed across the live Hale queue. The
  // Hale queue endpoint already filters to actionable + decided rows, so
  // these counts mirror exactly what's visible in the queue list.
  const summary = useMemo(() => {
    const c = { awaiting: 0, approved: 0, held: 0, rejected: 0, badges: 0 };
    for (const a of items) {
      const h = a.hale?.hale_status;
      if (h === 'badge_issued') { c.badges += 1; c.approved += 1; }
      else if (h === 'hale_approved') c.approved += 1;
      else if (h === 'hale_hold') c.held += 1;
      else if (h === 'hale_rejected') c.rejected += 1;
      else c.awaiting += 1;
    }
    return [
      { key: 'awaiting',  label: 'Awaiting Hale decision', value: c.awaiting },
      { key: 'approved',  label: 'Approved',               value: c.approved },
      { key: 'held',      label: 'Held / conditions',      value: c.held, tone: '#A07A14' },
      { key: 'rejected',  label: 'Rejected',               value: c.rejected, tone: '#B0312D' },
      { key: 'badges',    label: 'Badges issued',          value: c.badges, tone: '#16624B' },
    ];
  }, [items]);

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}
      data-testid="hale-decisions-page"
    >
      <Helmet><title>Hale Program Decisions · ToxIntel</title></Helmet>

      <header className="border-b" style={{ borderColor: RULE }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" aria-label="ToxIntel home">
              <span className="text-xl tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}>
                <TI color={CORAL} />
              </span>
            </Link>
            <span className="ml-3 text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-sm"
                  style={{ backgroundColor: SLATE, color: IVORY }}>
              · Admin · Hale · Program decisions
            </span>
          </div>
          <nav className="flex items-center gap-5">
            <Link to="/admin/hale" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="decisions-nav-back">
              ← Hale admin hub
            </Link>
            <Link to="/assessor/hale-review" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="decisions-nav-reviewer">
              ← AG reviewer queue
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 lg:py-14">
        <div className="mb-10">
          <HaleWordmark size="md" />
          <p className="mt-5 text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
            Hale program authority
          </p>
          <h1
            className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            data-testid="decisions-title"
          >
            Hale program decisions.
          </h1>
          <p className="mt-3 text-sm max-w-3xl" style={{ color: MUTED }}>
            Final Hale program decisions live here. AG scientific output (recommendation,
            released findings, conditions) is shown read-only. Hale decides whether to
            <span className="font-mono"> approve</span>,
            <span className="font-mono"> hold</span>, or
            <span className="font-mono"> reject</span> the program admission, and which
            badge (Reviewed™ / Verified™ / Both) to issue.
          </p>
        </div>

        {loading && <p className="text-sm font-mono uppercase tracking-wider" style={{ color: MUTED }} data-testid="decisions-loading">Loading queue…</p>}
        {error && (
          <div className="rounded-sm border p-5"
               style={{ borderColor: 'rgba(176,49,45,0.4)', backgroundColor: 'rgba(176,49,45,0.05)', color: '#B0312D' }}
               data-testid="decisions-error-banner">
            <Warning size={18} weight="fill" className="inline mr-2" /> {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Decision summary cards — computed from live queue. */}
            <SummaryCards items={summary} testidPrefix="decisions-summary" />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <aside className="lg:col-span-2">
              <div className="rounded-sm border" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}>
                <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: RULE }}>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: CORAL }}>
                    Hale queue · {items.length}
                  </p>
                </div>
                <ul data-testid="decisions-queue-list" className="max-h-[640px] overflow-y-auto">
                  {items.length === 0 && (
                    <li className="p-5 text-[13px] leading-relaxed" style={{ color: MUTED }} data-testid="decisions-empty">
                      No AG scientific outputs are pending Hale decision. New recommendations
                      will appear here after AG commits and routes an audit output.
                    </li>
                  )}
                  {items.map(a => (
                    <li
                      key={a.id}
                      className="p-4 border-b cursor-pointer hover:bg-white/60"
                      style={{ borderColor: RULE, backgroundColor: openId === a.id ? 'rgba(255,78,106,0.06)' : 'transparent' }}
                      onClick={() => setOpenId(a.id)}
                      data-testid={`decisions-queue-item-${a.id}`}
                    >
                      <p className="text-sm font-semibold" style={{ color: SLATE }}>{a.brand_name || '(unnamed brand)'}</p>
                      <p className="text-[11px] font-mono uppercase tracking-wider mt-1" style={{ color: MUTED }}>
                        {a.ag?.recommendation_label || 'AG pending'}
                      </p>
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        <StatusPill value={a.hale?.hale_status} options={options?.hale_statuses} accent={CORAL} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Decision criteria · checklist of pre-flight items Hale
                 should confirm before committing the program decision. */}
              <DecisionCriteriaPanel app={openApp} />
            </aside>

            <section className="lg:col-span-3">
              {!openApp && (
                <div className="rounded-sm border p-10 text-center"
                     style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}
                     data-testid="decisions-no-selection">
                  <Hourglass size={32} weight="duotone" style={{ color: CORAL }} className="mx-auto mb-3" />
                  <p className="text-base font-semibold" style={{ color: SLATE }}>Pick a submission to review the AG output and make a Hale decision.</p>
                </div>
              )}

              {openApp && (
                <div className="rounded-sm border p-7"
                     style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.7)' }}
                     data-testid="decisions-detail">
                  <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
                        Hale decision · application {openApp.id.slice(-6).toUpperCase()}
                      </p>
                      <h2 className="text-2xl font-extrabold tracking-tight mt-1"
                          style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                        {openApp.brand_name || '(unnamed brand)'}
                      </h2>
                    </div>
                    <div className="text-right">
                      <StatusPill value={openApp.hale?.hale_status} options={options?.hale_statuses} accent={CORAL} />
                      <p className="text-[10px] font-mono uppercase tracking-wider mt-2" style={{ color: MUTED }}>
                        Brand sees: {openApp.brand_status?.replaceAll('_', ' ')}
                      </p>
                    </div>
                  </div>

                  <div className="mb-5">
                    <AuditTimeline
                      stages={['Submitted', 'AG review', 'AG output complete', 'Hale decision pending', 'Badge issued']}
                      currentIndex={haleStageIndex(openApp)}
                      testid="decisions-audit-timeline"
                    />
                  </div>

                  <DecisionEditor app={openApp} onDecided={handleDecided} />
                </div>
              )}
            </section>
          </div>
          </>
        )}
      </main>
    </div>
  );
}

/* Decision Criteria · Hale pre-flight checklist.
 *
 * Computed from the live submission state so each row reflects what's
 * actually true on the record — AG recommendation received, findings
 * released, conditions present (when applicable), brand docs complete
 * (from the AG checklist), and the pathway track decision. Purely
 * advisory — Hale can still commit a decision even if a row is unmet. */
function DecisionCriteriaPanel({ app }) {
  const rows = useMemo(() => {
    if (!app) {
      return [
        'AG scientific recommendation received',
        'Required findings released',
        'Conditions reviewed',
        'Brand documentation complete',
        'Badge pathway confirmed: Reviewed™ / Verified™',
      ].map(label => ({ label, ok: false, neutral: true }));
    }
    const ag = app.ag || {};
    const hale = app.hale || {};
    const recHas = Boolean(ag.recommendation);
    const findingsHas = Boolean(ag.released_findings && ag.released_findings.trim().length);
    const conditionsRow = Array.isArray(ag.recommendation_conditions) && ag.recommendation_conditions.length;
    const conditionsReviewed = ag.recommendation !== 'recommended_with_conditions' || conditionsRow;
    const checklist = ag.document_checklist || [];
    const docsComplete = checklist.length > 0 &&
      checklist.every(r => r.status === 'complete' || r.status === 'not_applicable');
    const trackPicked = Boolean(hale.track) || hale.hale_status === 'badge_issued';
    return [
      { label: 'AG scientific recommendation received', ok: recHas },
      { label: 'Required findings released', ok: findingsHas },
      { label: 'Conditions reviewed', ok: conditionsReviewed },
      { label: 'Brand documentation complete', ok: docsComplete },
      { label: 'Badge pathway confirmed: Reviewed™ / Verified™', ok: trackPicked, neutral: !trackPicked && !['badge_issued','hale_approved'].includes(hale.hale_status) },
    ];
  }, [app]);

  return (
    <div
      className="rounded-sm border p-4 mt-4"
      style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}
      data-testid="decision-criteria-panel"
    >
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2.5" style={{ color: CORAL }}>
        Hale decision criteria
      </p>
      <ul className="space-y-2">
        {rows.map(r => (
          <li key={r.label} className="flex items-start gap-2.5" data-testid={`decision-criteria-${r.label.toLowerCase().replace(/[^a-z]+/g, '-').slice(0,30)}`}>
            <span
              className="inline-block w-4 h-4 mt-0.5 flex-shrink-0 rounded-full"
              style={{
                backgroundColor: r.ok ? '#16624B' : 'transparent',
                border: `1.5px solid ${r.ok ? '#16624B' : (r.neutral ? MUTED : '#B0312D')}`,
              }}
              aria-hidden="true"
            />
            <span className="text-[12.5px] leading-snug" style={{ color: r.ok ? SLATE : MUTED }}>
              {r.label}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[10px] mt-3 leading-snug" style={{ color: MUTED }}>
        Advisory only. Hale retains program authority and can decide even when
        criteria are unmet.
      </p>
    </div>
  );
}
